import React, {useEffect, useState, useRef} from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {toast} from 'react-toastify';
import './NoteDetails.css';
import { likePost, savePost, addUpdate } from '../../actions/postAction';
import Loader from '../../layout/Loder';
import Settingspopup from './Settingspopup';
import Sharelink from './Sharelink';
import Release from './Release';
import config from '../../config';
import { Helmet } from 'react-helmet';
import formatDate, { linkify } from '../../util';
import AiSuggestions from './AiSuggestions';
import StrategistChat from './StrategistChat';
import AssistChat from './AssistChat';
import FeedbackPrompt from './FeedbackPrompt';

const NoteDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "unknown";
  const {user} = useSelector((state) => state.user);
  const feedbackEligible = useSelector((state) => state.feedback.eligible);
  const {postids} = useSelector((state) => state.savedposts);
  const { likedposts } = useSelector((state) => state.likedposts);
  const {id} = useParams();
  const [post, setpost] = useState('');
  const [date, setdate] = useState();
  const [releasedate, setreleasedate] = useState();
  const [showsettings, setshowsettings] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showsharelink, setshowsharelink] = useState(false);
  const [saved, setsaved] = useState(postids.includes(id));
  const [likeCount, setLikeCount] = useState(0);
  const [borderProgress, setBorderProgress] = useState(0);
  const [timer, setTimer] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [plan, setPlan] = useState(null);
  const [showAiSuggest, setShowAiSuggest] = useState(false);
  const [showStrategist, setShowStrategist] = useState(false);
  const [showAssistChat, setShowAssistChat] = useState(false);
  const [chatMessages, setChatMessages] = useState(null); // null = not yet initialized
  const [strategistState, setStrategistState] = useState(null); // persisted strategist flow snapshot
  const [expandedAssist, setExpandedAssist] = useState(new Set());
  const [updateText, setUpdateText] = useState('');
  const [updateFocused, setUpdateFocused] = useState(false);
  const [updateSubmitting, setUpdateSubmitting] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [planRecords, setPlanRecords] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const assistLoadedFor = useRef(null);
  const updateRef = useRef(null);
  let updatedLikeCount = 0;

  const MS_IN_DAY = 1000 * 60 * 60 * 24;
  const updates = post?.updates || [];
  const today = new Date();
  const planStartDate = plan ? new Date(plan.createdAt) : null;
  const totalDays = plan ? (plan.durationInYears || 1) * 365 : null;
  const promisedDay = (planStartDate && totalDays)
    ? new Date(planStartDate.getTime() + totalDays * MS_IN_DAY)
    : null;
  const daysLeft = promisedDay
    ? Math.max(Math.floor((promisedDay - today) / MS_IN_DAY), 0)
    : null;
  const nodeCreatedDate = post ? new Date(post.date) : null;
  const nodeDaysPassed = nodeCreatedDate
    ? Math.max(Math.floor((today - nodeCreatedDate) / MS_IN_DAY), 0)
    : 0;
  const journeyPercent = totalDays
    ? Math.min(Math.max((nodeDaysPassed / totalDays) * 100, 0), 100)
    : null;

  const handleRelease = () => {
    setShowPopup(true);
    document.body.style.overflow = 'hidden';
  };

  const handleSettings = (e) => {
    e.preventDefault();
    setshowsettings(!showsettings);
  };

  const submitUpdate = async () => {
    if (!updateText.trim() || updateSubmitting) return;
    setUpdateSubmitting(true);
    try {
      await dispatch(addUpdate({ postId: post._id, updateNote: updateText.trim() }, (updatedPost) => {
        setpost(updatedPost);
        setPlanRecords(prev => prev.map(r => r._id === updatedPost._id ? updatedPost : r));
      }));
      setUpdateText('');
      setUpdateFocused(false);
      if (updateRef.current) updateRef.current.blur();
      if (feedbackEligible) setTimeout(() => setShowFeedback(true), 8000);
    } catch (error) {
      toast.error('Failed to add update.');
    } finally {
      setUpdateSubmitting(false);
    }
  };

  const shareLink = (e) => {
    e.preventDefault();
    setshowsharelink(!showsharelink);
  };

  const saveTooltip = <Tooltip id="save-tooltip">{postids.includes(id) ? "Unsave" : "Save"}</Tooltip>;
  const createdbyTooltip = <Tooltip id="createdby-tooltip">Created by</Tooltip>;
  const ttcTooltip = <Tooltip id="ttc-tooltip">Time to release</Tooltip>;

  const debounceLike = (func, timeout = 1000) => {
    return (...args) => {
      clearTimeout(timer);
      const newTimer = setTimeout(() => { func.apply(this, args); }, timeout);
      setTimer(newTimer);
    };
  };

  const likeunlike = async () => {
    try {
      await dispatch(likePost({ postId: post._id, likeCount: updatedLikeCount }));
    } catch (_) {
    }
  };

  const process = debounceLike(() => likeunlike());

  const handleClick = async () => {
    if (borderProgress < 100) {
      updatedLikeCount = likeCount + 1;
      setLikeCount(updatedLikeCount);
      post.likes = post.likes + 1;
      setBorderProgress((prev) => prev + 25);
      process();
    }
  };

  const saveunsave = async (e) => {
    e.preventDefault();
    setsaved(!saved);
    try {
      await dispatch(savePost({ postId: post._id }));
    } catch (error) {
      toast.error(error);
    }
  };

  // Fetch sibling records for sidebar whenever the current post's plan changes.
  useEffect(() => {
    if (!post || !post.planId) { setPlanRecords([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${config.apiUrl}/api/v1/yearly-plan/${post.planId}`,
          { withCredentials: true }
        );
        if (cancelled) return;
        const records = (data?.plan?.posts || [])
          .map((p) => p.postId)
          .filter(Boolean);
        setPlanRecords(records);
      } catch (_) {
        if (!cancelled) setPlanRecords([]);
      }
    })();
    return () => { cancelled = true; };
  }, [post && post.planId]);

  useEffect(() => {
    // Reset transient UI state when switching records via sidebar.
    setUpdateText('');
    setShowAssistChat(false);
    setShowStrategist(false);
    setShowAiSuggest(false);
    setShowStats(false);
    setExpandedAssist(new Set());
    setreleasedate(undefined);
    // Wipe Assist chat in-memory so previous record's history can't leak into
    // this one (or be written back to its localStorage entry).
    setChatMessages(null);
    setStrategistState(null);
    assistLoadedFor.current = null;

    // If we already have this record populated from the plan fetch, use it
    // directly. Sidebar navigation between sibling records becomes free.
    const cached = planRecords.find((r) => r && r._id === id);
    if (cached) {
      setpost(cached);
      const likesByUser = likedposts.find(lp => lp.post === cached._id)?.likes || 0;
      setBorderProgress(likesByUser * 25);
      setLikeCount(likesByUser);
      setdate(formatDate(new Date(cached.date)));
      if (cached.status === 'released' && cached.releaseDate) {
        setreleasedate(formatDate(new Date(cached.releaseDate)));
      }
    } else {
      const fetchPost = async () => {
        const serverurl = config.apiUrl;
        try {
          const {data} = await axios.get(`${serverurl}/api/v1/myposts/${id}`, {withCredentials: true});
          setpost(data.post);
          if (data.plan) setPlan(data.plan);
          const likesByUser = likedposts.find(lp => lp.post === data.post._id)?.likes || 0;
          setBorderProgress(likesByUser * 25);
          setLikeCount(likesByUser);
          setdate(formatDate(new Date(data.post.date)));
          if (data.post.status === 'released') {
            setreleasedate(formatDate(new Date(data.post.releaseDate)));
          }
        } catch (error) {
          toast.error(error.response.data.message);
          navigate("/dashboard");
        }
      };
      fetchPost();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Restore persisted Dmyor Assist state (chat messages + strategist flow snapshot)
  // keyed by node id, so panel close + browser refresh don't lose user work.
  useEffect(() => {
    if (!post?._id) return;
    try {
      const saved = localStorage.getItem(`dmyor_assist_${post._id}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.messages) setChatMessages(parsed.messages);
        if (parsed.strategistState) setStrategistState(parsed.strategistState);
      }
    } catch (_) { /* corrupted entry — ignore */ }
    // Mark "loaded for this id" so the persist effect can run safely without
    // clobbering this note's stored data with the previous note's state.
    assistLoadedFor.current = post._id;
  }, [post?._id]);

  // Persist on change. Removes entry when both slots are empty so stale data clears.
  useEffect(() => {
    if (!post?._id) return;
    // Guard: do not write until the load effect has finished for THIS note.
    // Prevents stale state from a previous note overwriting this note's storage.
    if (assistLoadedFor.current !== post._id) return;
    const key = `dmyor_assist_${post._id}`;
    if (chatMessages === null && strategistState === null) {
      localStorage.removeItem(key);
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify({ messages: chatMessages, strategistState }));
    } catch (_) { /* quota exceeded — silent */ }
  }, [post?._id, chatMessages, strategistState]);


  const isOwner = post && user && post.user.toString() === user._id.toString();
  const isOther = post && user && post.user.toString() !== user._id.toString();

  return (
    <>
      <Helmet>
        <title>{`Record - ${post._id}`}</title>
        <meta name="description" content={post.note} />
        <meta property="og:title" content="See this Record" />
        <meta property="og:description" content={post.note} />
        <meta property="og:url" content={`https://www.dmyor.com/dashboard/note-details/${post._id}`} />
        <meta property="og:type" content="website" />
      </Helmet>

      {post ? (
        <div className={`nd-outer ${showAssistChat ? "nd-outer--chat-open" : ""} ${planRecords.length > 0 ? "nd-outer--with-sidebar" : ""}`}>

        {planRecords.length > 0 && (
          <>
            <button
              type="button"
              className="nd-sidebar-toggle"
              onClick={() => setSidebarOpen((v) => !v)}
              aria-label="Toggle records list"
            >
              ☰
            </button>

            {sidebarOpen && (
              <div className="nd-sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
            )}

            <aside className={`nd-sidebar ${sidebarOpen ? "nd-sidebar--open" : ""}`}>
              <div className="nd-sidebar-vision">
                <button
                  type="button"
                  className="nd-sidebar-vision-name"
                  onClick={() => navigate(`/dashboard/year-plan/${post.planId}`)}
                  title="Open Vision"
                >
                  {plan?.name || plan?.heading || "Vision"}
                </button>
                <div className="nd-sidebar-actions">
                  <button
                    type="button"
                    className="nd-sidebar-action"
                    onClick={() => navigate(`/dashboard/start/vision-setup/${post.planId}`)}
                  >
                    Add Record
                  </button>
                  <button
                    type="button"
                    className="nd-sidebar-action"
                    onClick={() => navigate(`/dashboard/start/vision-setup/${post.planId}`)}
                    title="Edit Vision"
                  >
                    <i className="bx bx-edit-alt" /> Edit
                  </button>
                  <button
                    type="button"
                    className="nd-sidebar-action"
                    onClick={() => navigate(`/dashboard/year-plan/${post.planId}/todos`)}
                    title="Vision Todos"
                  >
                    <i className="bx bx-check-square" /> Todos
                  </button>
                  <button
                    type="button"
                    className="nd-sidebar-action"
                    onClick={() => navigate(`/dashboard/year-plan/${post.planId}/analysis`)}
                    title="Vision Report"
                  >
                    <i className="bx bx-bar-chart-alt-2" /> Report
                  </button>
                </div>
              </div>
              <div className="nd-sidebar-head">
                <span className="nd-sidebar-title">Records in this Vision</span>
                <button
                  type="button"
                  className="nd-sidebar-close"
                  onClick={() => setSidebarOpen(false)}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <ul className="nd-sidebar-list">
                {planRecords.map((r) => {
                  const isActive = r._id === post._id;
                  const isThoughts = r.category === "Thoughts";
                  const empty = !r.note || !r.note.trim();
                  const preview = isThoughts && empty
                    ? "your thoughts thread…"
                    : (r.note || "").slice(0, 90) + ((r.note || "").length > 90 ? "…" : "");
                  return (
                    <li key={r._id}>
                      <button
                        type="button"
                        className={`nd-sidebar-item${isActive ? " active" : ""}${isThoughts && empty ? " thoughts" : ""}`}
                        onClick={() => {
                          setSidebarOpen(false);
                          if (!isActive) navigate(`/dashboard/note-details/${r._id}`);
                        }}
                      >
                        <span className={`nd-sidebar-dot ${r.status || "active"}`} />
                        <span className="nd-sidebar-item-text">{preview || "untitled"}</span>
                        {r.category && (
                          <span className="nd-sidebar-item-cat">{r.category}</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>
          </>
        )}

        <div className="nd-page">

          {/* ── BACK ── */}
          <button
            className="note-item-back"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            ← Back
          </button>

          {/* ── TOP BAR ── */}
          <div className="nd-topbar">
            <div className="nd-topbar-left">
              {post.category === "side-quest" && (
                <span className="nd-sq-tag">◈ Side Quest</span>
              )}
              {post.category && post.category !== "side-quest" && (
                <span className="nd-category-chip">{post.category}</span>
              )}
              {post.planId && (
                <button
                  className="nd-plan-link"
                  onClick={() => navigate(`/dashboard/year-plan/${post.planId}`)}
                  aria-label="Go to Vision"
                >
                  <i className="bx bx-map-alt" />
                  Vision
                </button>
              )}
            </div>

            <div className="nd-topbar-icons">
              {isOther && (
                <OverlayTrigger placement="top" overlay={saveTooltip}>
                  <span className="nd-icon-wrap" onClick={saveunsave}>
                    <i className={`bx ${saved ? "bxs-bookmark" : "bx-bookmark"} nd-icon ${saved ? "saved" : ""}`} />
                  </span>
                </OverlayTrigger>
              )}
              {isOwner && post.status !== "released" && (
                <button className="nd-strategist-btn" onClick={() => setShowAssistChat((v) => !v)}>
                  <i className="bx bx-bulb" /> Assist
                </button>
              )}
              {isOwner && user?.aiAccess && post.status !== "released" && (
                <span className="nd-icon-wrap" onClick={() => { localStorage.setItem('dmyor_gs_analysis', '1'); navigate(`/dashboard/note-details/${post._id}/analysis`); }}>
                  <i className="bx bx-bar-chart-alt-2 nd-icon" title="Journey Analysis" />
                </span>
              )}
              {isOwner && (
                <span className="nd-icon-wrap" onClick={handleSettings}>
                  <i className="bx bx-cog nd-icon" />
                </span>
              )}
              {post.method === "digicoins" && (
                <span className="nd-icon-wrap" onClick={shareLink}>
                  <i className="bx bx-share-alt nd-icon" />
                </span>
              )}
            </div>
          </div>

          {/* ── RELEASE META ── */}
          {post.status === "released" && (post.username || post.totalTime) && (
            <div className="nd-release-meta">
              {post.username && (
                <span className="nd-release-meta-item">
                  <i className="bx bxs-user" /> {post.username}
                </span>
              )}
              {post.username && post.totalTime && (
                <span className="nd-release-meta-sep">·</span>
              )}
              {post.totalTime && (
                <span className="nd-release-meta-item">
                  <i className="bx bx-time" /> {post.totalTime}
                </span>
              )}
            </div>
          )}

          {/* ── STATS ── */}
          {post.planId && (
            <div className="nd-stats-wrap">
              <button
                className="nd-stats-toggle"
                onClick={() => setShowStats((p) => !p)}
              >
                {showStats ? "Hide Stats" : "View Stats"}
                <span className={`nd-stats-chevron ${showStats ? "open" : ""}`}>›</span>
              </button>

              {showStats && (
                <div className="nd-stats-grid">
                  {/* <div className="nd-stat-card">
                    <span className="nd-stat-card-value">{nodeDaysPassed}</span>
                    <span className="nd-stat-card-label">Days travelled</span>
                  </div> */}
                  {daysLeft !== null && (
                    <div className="nd-stat-card">
                      <span className="nd-stat-card-value">{daysLeft}</span>
                      <span className="nd-stat-card-label">Days remaining</span>
                    </div>
                  )}
                  {journeyPercent !== null && (
                    <div className="nd-stat-card">
                      <span className="nd-stat-card-value">{journeyPercent.toFixed(1)}%</span>
                      <span className="nd-stat-card-label">Complete</span>
                    </div>
                  )}
                  <div className="nd-stat-card">
                    <span className="nd-stat-card-value">{updates.length}</span>
                    <span className="nd-stat-card-label">Updates</span>
                  </div>
                  <div className="nd-stat-card">
                    <span className="nd-stat-card-value">
                      {updates.length > 0 && nodeDaysPassed > 0
                        ? `~${Math.round(nodeDaysPassed / updates.length)}d`
                        : "—"}
                    </span>
                    <span className="nd-stat-card-label">Avg frequency</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── THREAD ── */}
          <div className="nd-thread">

            <div className="nd-thread-item nd-original">
              <div className="nd-thread-left">
                <div className={`nd-dot ${post.status}`} />
                {(post.updates?.length > 0 || post.releasenote) && (
                  <div className="nd-line" />
                )}
              </div>
              <div className="nd-thread-body">
                <div className="nd-thread-meta">
                  <span className={`nd-status-label ${post.status}`}>
                    {post.status === "released" ? "Released" : post.status === "active" ? "Active" : "Standby"}
                  </span>
                  <span className="nd-thread-date">
                    {post.status === "released" ? `${date} → ${releasedate}` : date}
                  </span>
                </div>
                {post.note && post.note.trim().length > 0 && (
                  <div className="nd-thread-text">
                    {post.note.split('\n').map((line, i) => (
                      <p key={i}>{linkify(line)}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* THOUGHTS EMPTY-STATE EXPLAINER */}
            {post.category === "Thoughts" && !post.note?.trim() && (!post.updates || post.updates.length === 0) && (
              <div className="nd-thoughts-empty">
                <p className="nd-thoughts-empty-title">This is your thoughts record.</p>
                <p className="nd-thoughts-empty-text">
                  Free-form space for whatever you are thinking, feeling, or noticing about this vision. Not strategy. Not tasks. Just thoughts. Post an update anytime you have something to say to future-you.
                </p>
              </div>
            )}

            {/* UPDATES */}
            {post.updates && post.updates.length > 0 && (
              post.updates.map((update, index) => {
                const isAssist = update.update.startsWith("[Dmyor Assist]");
                const isExpanded = expandedAssist.has(index);

                // Parse Dmyor Assist JSON payload
                let assistData = null;
                if (isAssist) {
                  try {
                    const raw = update.update.replace(/^\[Dmyor Assist\]\s*/, "");
                    assistData = JSON.parse(raw);
                  } catch (_) {
                    // Legacy format — plain text assist
                    assistData = { reasoning: "", actions: [update.update.replace(/^\[Dmyor Assist\]\s*/, "")] };
                  }
                }

                const toggleAssist = () => {
                  setExpandedAssist((prev) => {
                    const next = new Set(prev);
                    if (next.has(index)) next.delete(index);
                    else next.add(index);
                    return next;
                  });
                };

                return (
                <div className="nd-thread-item" key={index}>
                  <div className="nd-thread-left">
                    <div className={`nd-dot ${isAssist ? "assist" : post.status}`} />
                    {(index < post.updates.length - 1 || post.releasenote) && (
                      <div className="nd-line" />
                    )}
                  </div>
                  <div className="nd-thread-body">
                    {isAssist && assistData ? (
                      <div className="nd-assist-card">
                        <div className="nd-assist-header" onClick={toggleAssist}>
                          <div className="nd-assist-header-left">
                            <span className="nd-assist-badge">
                              <i className="bx bx-bulb" />
                            </span>
                            <div className="nd-assist-header-text">
                              <span className="nd-assist-label">Dmyor Assist</span>
                              <span className="nd-assist-meta">
                                {formatDate(new Date(update.date))} · {assistData.actions.length} action{assistData.actions.length !== 1 ? "s" : ""}
                              </span>
                            </div>
                          </div>
                          <i className={`bx bx-chevron-down nd-assist-chevron ${isExpanded ? "open" : ""}`} />
                        </div>
                        {isExpanded && (
                          <div className="nd-assist-body">
                            {assistData.reasoning && (
                              <p className="nd-assist-reasoning">{assistData.reasoning}</p>
                            )}
                            <div className="nd-assist-actions-list">
                              {assistData.actions.map((action, i) => (
                                <div className="nd-assist-action-item" key={i}>
                                  <div className="nd-assist-action-num">{i + 1}</div>
                                  <span className="nd-assist-action-text">{action}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="nd-thread-meta">
                          <span className="nd-update-label">Update</span>
                          <span className="nd-thread-date">
                            {formatDate(new Date(update.date))}
                          </span>
                        </div>
                        <div className="nd-thread-text">
                          {update.update.split('\n').map((line, i) => (
                            <p key={i}>{linkify(line)}</p>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                );
              })
            )}

            {/* RELEASE NOTE */}
            {post.releasenote && (
              <div className="nd-thread-item nd-release">
                <div className="nd-thread-left">
                  <div className="nd-dot released" />
                </div>
                <div className="nd-thread-body">
                  <div className="nd-thread-meta">
                    <span className="nd-status-label released">Released</span>
                    <span className="nd-thread-date">{releasedate}</span>
                  </div>
                  <div className="nd-thread-text nd-releasenote-text">
                    {post.releasenote.split('\n').map((line, i) => (
                      <p key={i}>{linkify(line)}</p>
                    ))}
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* ── INLINE UPDATE COMPOSER ── */}
          {isOwner && post.status !== "released" && (
            <div className={`nd-composer ${updateFocused ? "focused" : ""}`}>
              <div className="nd-composer-inner">
                <textarea
                  ref={updateRef}
                  className="nd-composer-input"
                  placeholder="What's happenin?..."
                  value={updateText}
                  onChange={(e) => setUpdateText(e.target.value)}
                  onFocus={() => setUpdateFocused(true)}
                  onBlur={() => { if (!updateText.trim()) setUpdateFocused(false); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submitUpdate();
                  }}
                  rows={updateFocused ? 3 : 1}
                />
                {updateFocused && (
                  <div className="nd-composer-actions">
                    <span className="nd-composer-hint">⌘↵ to post</span>
                    <button
                      className="nd-composer-btn"
                      onClick={submitUpdate}
                      disabled={!updateText.trim() || updateSubmitting}
                    >
                      {updateSubmitting ? 'Posting...' : 'Post'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── MODALS ── */}
          {showPopup && (
            <div className="overlay">
              <i className="bx bx-x overlay-close" onClick={() => setShowPopup(false)} />
              <Release setShowPopup={setShowPopup} post={post} setpost={setpost} setreleasedate={setreleasedate} user={user} />
            </div>
          )}
          {showsettings && (
            <div className="overlay">
              <i className="bx bx-x overlay-close" onClick={() => setshowsettings(false)} />
              <Settingspopup
                post={post}
                setpost={setpost}
                closeSettings={() => setshowsettings(false)}
                onRelease={() => { setshowsettings(false); handleRelease(); }}
              />
            </div>
          )}
          {showsharelink && (
            <Sharelink setshowsharelink={setshowsharelink} postId={post._id} />
          )}
          {showAiSuggest && (
            <AiSuggestions
              nodeId={post._id}
              onClose={() => setShowAiSuggest(false)}
              onApplied={async () => {
                const { data } = await axios.get(`${config.apiUrl}/api/v1/myposts/${id}`, { withCredentials: true });
                setpost(data.post);
              }}
            />
          )}
          {showStrategist && (
            <StrategistChat
              nodeId={post._id}
              onClose={() => setShowStrategist(false)}
              onApplied={async () => {
                const { data } = await axios.get(`${config.apiUrl}/api/v1/myposts/${id}`, { withCredentials: true });
                setpost(data.post);
              }}
            />
          )}

        </div>

        {showAssistChat && post.status !== "released" && (
          <AssistChat
            key={post._id}
            nodeId={post._id}
            onClose={() => setShowAssistChat(false)}
            messages={chatMessages}
            onMessagesChange={setChatMessages}
            strategistState={strategistState}
            onStrategistStateChange={setStrategistState}
            updates={post.updates || []}
            onApplied={async () => {
              const { data } = await axios.get(`${config.apiUrl}/api/v1/myposts/${id}`, { withCredentials: true });
              setpost(data.post);
            }}
          />
        )}

        </div>
      ) : (
        <Loader />
      )}

      {showFeedback && (
        <FeedbackPrompt trigger="node_update" onDone={() => setShowFeedback(false)} />
      )}
    </>
  );
};

export default NoteDetails;
